import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useOutletContext } from "react-router-dom";
import { MarbleBoard } from "../components/dashboard/marble/MarbleBoard";
import {
  Calendar, Users, Car, Plus, ArrowUpRight, ArrowDownRight,
  MapPin, ChevronRight, Bell, FileText, Wrench, Shield,
  CalendarClock, BadgeCheck, CircleDollarSign, Cloud,
  Download, Upload, Wallet, TrendingUp, Percent,
  Sun, Moon, CloudSun, CloudMoon, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning
} from "lucide-react";
import { StatusBadge } from "../components/StatusBadge";
import AssistantBar from "../components/AssistantBar";
import { LiveServicesSection } from "../components/service-progress/LiveServicesSection";
import { useOps, ExecPanel, OpsStatus, NextServices, QuickAlerts, EconomicSummary } from "../components/dashboard/OpsOverview";
import { DashboardGpsMap } from "../components/dashboard/DashboardGpsMap";
import { Podium } from "../components/dashboard/Podium";
import { ExecBoard } from "../components/dashboard/exec/ExecBoard";
import {
  ResponsiveContainer, LineChart, Line, Area, AreaChart, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, Legend
} from "recharts";

const WEATHER_ICON_MAP = {
  "sun": Sun, "moon": Moon, "cloud-sun": CloudSun, "cloud-moon": CloudMoon, "cloud": Cloud, "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle, "cloud-rain": CloudRain, "cloud-snow": CloudSnow, "cloud-lightning": CloudLightning,
};
const PIE_COLORS = ["#D4A72C", "#F2C85B", "#FFD76A", "#8E6E1A", "#B8891A", "#765713"];
const RANGES = { 7: "7 giorni", 30: "30 giorni", month: "Mese corrente" };

const fmtDate = (iso) => { if (!iso) return "-"; const [y,m,d] = iso.split("-"); return `${d}/${m}/${y}`; };
const initials = (a="", b="") => `${(a[0]||"").toUpperCase()}${(b[0]||"").toUpperCase()}`;

export default function Dashboard() {
  const { user } = useAuth();
  const isDriver = user?.role === "autista";
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [exp, setExp] = useState({ vehicles: [], drivers: [], operator: [] });
  const [topCust, setTopCust] = useState([]);
  const [topDrv, setTopDrv] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [driverExchanges, setDriverExchanges] = useState([]);
  const [range, setRange] = useState(7);
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [execData, setExecData] = useState(null);
  const outlet = useOutletContext() || {};

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  // Weather Live Roma (Open-Meteo via backend proxy, refresh ogni 10 minuti)
  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async () => {
      try {
        const r = await api.get("/weather/rome");
        if (!cancelled) setWeather(r.data);
      } catch (_) { /* keep previous value or null */ }
    };
    fetchWeather();
    const t = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth() + 1;
    Promise.allSettled([
      api.get("/dashboard/summary"),
      api.get("/dashboard/expirations", { params: { days: 60 } }),
      api.get(`/reports/customer-monthly`, { params: { year: y, month: m } }),
      api.get(`/reports/driver-monthly`, { params: { year: y, month: m } }),
      api.get("/drivers"), api.get("/customers"), api.get("/vehicles"),
      api.get("/notifications", { params: { unread_only: false } }),
      isDriver ? api.get("/exchanges/mine").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]).then(([s, e, tc, td, dr, cu, ve, nt, dx]) => {
      if (s.status === "fulfilled") setD(s.value.data);
      if (e.status === "fulfilled") setExp(e.value.data);
      if (tc.status === "fulfilled") setTopCust(tc.value.data.rows || []);
      if (td.status === "fulfilled") setTopDrv(td.value.data.rows || []);
      if (dr.status === "fulfilled") setDrivers(dr.value.data);
      if (cu.status === "fulfilled") setCustomers(cu.value.data);
      if (ve.status === "fulfilled") setVehicles(ve.value.data);
      if (nt.status === "fulfilled") setNotifs(nt.value.data || []);
      if (dx.status === "fulfilled") setDriverExchanges(dx.value.data || []);
    });
  }, [isDriver]);

  const findDriver = (id) => drivers.find(x => x.id === id);
  const findCustomer = (id) => customers.find(x => x.id === id);

  const daily = d?.daily_series || [];
  const rangeData = useMemo(() => {
    if (range === 7) return daily.slice(-7);
    if (range === 30) return daily;
    const ym = new Date().toISOString().slice(0, 7);
    return daily.filter(x => x.date?.startsWith(ym));
  }, [daily, range]);
  const rangeTotal = rangeData.reduce((a, x) => a + Number(x.revenue || 0), 0);
  const half = Math.floor(rangeData.length / 2);
  const rangePrev = rangeData.slice(0, half).reduce((a, x) => a + Number(x.revenue || 0), 0);
  const rangeCurr = rangeData.slice(half).reduce((a, x) => a + Number(x.revenue || 0), 0);
  const rangeDelta = rangePrev > 0 ? Math.round(((rangeCurr - rangePrev) / rangePrev) * 100) : 0;

  const yesterdayRev = daily[daily.length - 2]?.revenue || 0;
  const todayRev = daily[daily.length - 1]?.revenue || d?.revenue_month ? (daily[daily.length - 1]?.revenue || 0) : 0;
  const revDelta = yesterdayRev > 0 ? Math.round(((todayRev - yesterdayRev) / yesterdayRev) * 100) : 0;
  const vehiclesTotal = d?.vehicles_total || 0;
  const vehiclesBusy = d?.vehicles_busy || 0;
  const activePct = vehiclesTotal > 0 ? Math.round((vehiclesBusy / vehiclesTotal) * 100) : 0;

  const expAll = useMemo(() => {
    const items = [];
    (exp.vehicles || []).forEach(v => (v.expirations || []).forEach(e => items.push({ kind: "vehicle", subject: `${v.brand} ${v.model} · ${v.plate}`, ...e })));
    (exp.drivers || []).forEach(dr => (dr.expirations || []).forEach(e => items.push({ kind: "driver", subject: `${dr.first_name} ${dr.last_name}`, ...e })));
    (exp.operator || []).forEach(e => items.push({ kind: "operator", subject: e.subject || "Operatore", ...e }));
    (exp.items || []).forEach(e => items.push({ ...e, subject: e.entity_name, label: e.doc_type, date: e.expiry_date }));
    return items.sort((a, b) => (a.days ?? 999) - (b.days ?? 999));
  }, [exp]);

  const timeLabel = now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const dateLabel = now.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const displayName = user?.name || user?.email || "Utente";

  return (
    <div className={`space-y-4 sm:space-y-5 overflow-x-hidden ${!isDriver ? "mb-page -m-4 lg:-m-8 p-3 lg:p-5 pb-28 min-h-screen" : "pb-16 max-w-full"}`} data-testid="dashboard-page">
      {isDriver ? <Hero dateLabel={dateLabel} timeLabel={timeLabel} displayName={displayName} weather={weather} onNew={() => nav("/prenotazioni")} />
        : <>
          <MarbleBoard user={user} tools={outlet.heroTools} weather={weather} expirations={expAll} exec={execData} />
          <ExecBoard compact weather={weather} expirations={expAll} onData={setExecData} />
        </>}

      {isDriver ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI icon={Calendar} label="Servizi oggi" value={d?.today_count ?? 0} delta={null} hint={`${d?.upcoming_count ?? 0} in arrivo`}
               sparkline={daily.slice(-7).map(() => ({ v: 1 }))}/>
          <KPI icon={CircleDollarSign} label="Fatturato oggi" value={fmtEUR(todayRev)} delta={revDelta} hint={`${fmtEUR(d?.revenue_month || 0)} mese`}
               sparkline={daily.slice(-14).map(x => ({ v: x.revenue }))}/>
          <KPI icon={Users} label="Clienti serviti" value={customers.length} delta={null} hint={`${d?.today_count ?? 0} oggi`}
               sparkline={daily.slice(-7).map(() => ({ v: 1 }))}/>
          <VehiclesKPI busy={vehiclesBusy} total={vehiclesTotal} pct={activePct}/>
        </div>
      ) : null}

      {isDriver && driverExchanges.length > 0 && <DriverExchanges items={driverExchanges} />}

      {!isDriver && (
        <ExecPanel title="Andamento economico" sub="Dati reali del gestionale" action="Dettagli" onAction={() => nav("/conteggi")} testid="economic-section">
          <div className="grid grid-cols-1 lg:grid-cols-3">
            <div className="lg:border-r border-white/5"><EconomicSummary d={d} todayRev={todayRev} /></div>
            <div className="lg:col-span-2 p-4 sm:p-5 border-t lg:border-t-0 border-white/5 min-w-0">
              <RevenueTrend rangeData={rangeData} range={range} setRange={setRange} rangeTotal={rangeTotal} rangeDelta={rangeDelta} />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 border-t border-white/5">
            <div className="lg:col-span-2 p-4 sm:p-5"><FinancialSituation d={d}/></div>
            <div className="p-4 sm:p-5 border-t lg:border-t-0 lg:border-l border-white/5"><ServiceTypes data={d?.services_by_type} /></div>
          </div>
        </ExecPanel>
      )}

      {/* ===== Altri strumenti (sezioni esistenti, invariate nei dati) ===== */}
      <div className="pt-2 flex items-center gap-3" data-testid="dashboard-more-tools">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E9C46A] font-semibold whitespace-nowrap">Altri strumenti</div>
        <div className="gold-divider flex-1 opacity-50"/>
      </div>

      {!isDriver && <div id="nx-assistant-bar" className="scroll-mt-24"><AssistantBar /></div>}
      {!isDriver && <OpsBottom d={d} />}

      <div className="flex flex-wrap gap-2">
        <NavButton onClick={() => nav("/prenotazioni")} icon={Calendar} label="Servizi"/>
        <NavButton onClick={() => nav("/clienti")} icon={Users} label="Clienti"/>
        <NavButton onClick={() => nav("/turnistica")} icon={CalendarClock} label="Turistica"/>
        <NavButton onClick={() => nav("/conteggi")} icon={FileText} label="Report"/>
        <button type="button" onClick={() => document.getElementById("servizi-live")?.scrollIntoView({ behavior: "smooth" })} data-testid="dashboard-live-link" className="inline-flex items-center gap-2 rounded-xl border border-amber-500/50 text-amber-200 font-semibold px-4 min-h-[40px] text-sm hover:bg-amber-500/10">● Servizi Live</button>
      </div>

      {isDriver && (
        <ExecPanel title="Andamento fatturato" sub="Analisi" testid="driver-revenue-trend">
          <div className="p-4 sm:p-5"><RevenueTrend rangeData={rangeData} range={range} setRange={setRange} rangeTotal={rangeTotal} rangeDelta={rangeDelta} /></div>
        </ExecPanel>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Podium title="Top Clienti" sub="Mese corrente" testid="top-clienti" emptyText="Nessun cliente questo mese" onAll={() => nav("/clienti")}
          items={topCust.slice(0, 5).map((c, i) => ({ key: c.customer_id || i, name: c.customer_name, sub: `${c.services_count} servizi`, amount: fmtEUR(c.total_gross),
            initials: initials(...(c.customer_name || "").split(" ")), avatar: null, onClick: c.customer_id && c.customer_id !== "__anonimo__" ? () => nav(`/clienti/${c.customer_id}`) : null }))}/>
        <Podium title="Top Driver" sub="Mese corrente" testid="top-autisti" emptyText="Nessun autista attivo questo mese" onAll={() => nav("/conteggi")}
          items={topDrv.slice(0, 5).map((dRow, i) => { const drv = findDriver(dRow.driver_id); return ({ key: dRow.driver_id || i, name: dRow.driver_name, sub: `${dRow.services_count} servizi`,
            amount: fmtEUR(dRow.total_due), initials: drv ? initials(drv.first_name, drv.last_name) : "?", avatar: drv?.photo_url, onClick: null }); })}/>
        <TodayAgenda items={d?.upcoming} findCustomer={findCustomer} onOpen={() => nav("/prenotazioni")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Expirations items={expAll} unread={notifs.filter(n => !n.read).length} nav={nav} />
        <VehicleStatus vehicles={vehicles} onManage={() => nav("/flotta")} />
      </div>

      <LiveServicesSection />
    </div>
  );
}

function Hero({ dateLabel, timeLabel, displayName, weather, onNew }) {
  const WIcon = weather?.icon && WEATHER_ICON_MAP[weather.icon] ? WEATHER_ICON_MAP[weather.icon] : Cloud;
  const cond = weather?.condition || "Caricamento…";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#D4A72C]/35 px-4 py-5 sm:px-7 sm:py-6 hero-roma hero-exec luxe-fade-in" data-testid="dashboard-hero">
      <div className="relative flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#E9C46A] font-semibold capitalize" data-testid="dashboard-hero-date">{dateLabel} · {timeLabel}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-1.5 leading-[1.08]">
            Bentornato, <span className="text-gold-gradient italic break-words">{displayName}</span>
          </h1>
          <div className="mt-2 text-slate-300/90 text-xs sm:text-sm italic border-l-2 border-[#D4A72C]/60 pl-3 max-w-md">"Un servizio eccellente trasforma un viaggio in un'esperienza."</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 rounded-xl px-3 min-h-[44px] bg-black/60 backdrop-blur-md border border-[#D4A72C]/30" data-testid="weather-widget"
               title={weather ? `${cond} · Vento ${weather.wind_kmh} km/h · Fonte: Open-Meteo` : ""}>
            <WIcon className="text-[#E9C46A]" size={18} strokeWidth={1.6} data-testid="weather-icon"/>
            <div className="leading-tight">
              <div className="text-white font-mono-num font-bold text-sm" data-testid="weather-temp">{weather?.temperature != null ? `${weather.temperature}°C` : "—"}</div>
              <div className="text-[10px] text-slate-400 truncate max-w-[90px]" data-testid="weather-condition">{cond}</div>
            </div>
          </div>
          <button onClick={onNew} data-testid="dashboard-new-booking-btn" className="inline-flex items-center gap-2 rounded-xl btn-gold-primary font-bold px-4 min-h-[44px] text-sm whitespace-nowrap">
            <Plus size={16} strokeWidth={3}/> Nuova prenotazione
          </button>
        </div>
      </div>
    </div>
  );
}

function ExecKPI({ icon: Icon, label, value, hint, cls = "text-white", testid, className = "", onClick }) {
  return (
    <button type="button" onClick={onClick} className={`exec-card text-left p-3.5 sm:p-4 min-w-0 hover:border-[#D4A72C]/45 transition-colors ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-400 leading-tight">{label}</div>
        <Icon size={15} className="text-[#D4A72C] flex-shrink-0"/>
      </div>
      <div className={`font-mono-num font-extrabold text-xl sm:text-2xl mt-1.5 leading-none whitespace-nowrap ${cls}`} data-testid={`${testid}-value`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-1.5 leading-tight">{hint}</div>}
    </button>
  );
}

function ExecKPIs({ d }) {
  const nav = useNavigate();
  const ms = d?.monthly_series || [];
  const ym = new Date().toISOString().slice(0, 7);
  const idx = ms.findIndex(x => x.month === ym);
  const prev = idx > 0 ? ms[idx - 1]?.revenue : null;
  const delta = prev ? Math.round((((d?.revenue_month ?? 0) - prev) / prev) * 100) : null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3" data-testid="dashboard-kpis">
      <ExecKPI icon={Calendar} label="Servizi oggi" value={d?.today_count ?? 0} hint={`${d?.upcoming_count ?? 0} in arrivo`} cls="text-gold-metal" testid="kpi-services-today" className="col-span-2 lg:col-span-1" onClick={() => nav("/prenotazioni")}/>
      <ExecKPI icon={TrendingUp} label="Fatturato mese" value={fmtEUR(d?.revenue_month ?? 0)} hint={delta != null ? `${delta > 0 ? "+" : ""}${delta}% vs mese prec.` : "Mese corrente"} cls="text-gold-metal" testid="kpi-revenue-month" onClick={() => nav("/conteggi")}/>
      <ExecKPI icon={Percent} label="Margine mese" value={d?.margin_month != null ? fmtEUR(d.margin_month) : "Non disponibile"} hint="Mese corrente" testid="kpi-margin-month" onClick={() => nav("/conteggi")}/>
      <ExecKPI icon={Download} label="Da ricevere" value={fmtEUR(d?.outstanding_from_customers ?? 0)} hint="Crediti clienti" cls="text-emerald-300" testid="kpi-to-receive" onClick={() => nav("/conteggi")}/>
      <ExecKPI icon={Upload} label="Da pagare" value={fmtEUR(d?.outstanding_to_suppliers ?? 0)} hint="Debiti fornitori" cls="text-rose-300" testid="kpi-to-pay" onClick={() => nav("/conteggi")}/>
    </div>
  );
}

function OpsBottom({ d }) {
  const o = useOps();
  if (!o) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <QuickAlerts o={o} />
      <OpsStatus o={o} d={d} />
    </div>
  );
}

function RevenueTrend({ rangeData, range, setRange, rangeTotal, rangeDelta }) {
  return (
    <div data-testid="revenue-trend">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[11px] text-slate-400">Andamento fatturato</div>
        <div className="flex gap-1 bg-black/40 border border-white/5 rounded-lg p-1">
          {Object.entries(RANGES).map(([k, l]) => (
            <button key={k} onClick={() => setRange(k === "month" ? "month" : Number(k))} data-testid={`revenue-range-${k}`}
              className={`px-2.5 min-h-[30px] text-[11px] rounded-md font-semibold transition-colors ${String(range) === String(k) ? "bg-[#D4A72C] text-black" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 mb-2">
        <div className="font-mono-num text-xl font-extrabold text-[#E9C46A]" data-testid="revenue-range-total">{fmtEUR(rangeTotal)}</div>
        <div className={`text-xs font-bold flex items-center gap-1 ${rangeDelta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
          {rangeDelta >= 0 ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
          {rangeDelta > 0 ? "+" : ""}{rangeDelta}% <span className="text-slate-500 font-normal">vs periodo prec.</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={190}>
        <AreaChart data={rangeData} margin={{ left: -18, right: 4, top: 4 }}>
          <defs>
            <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4A72C" stopOpacity={0.45}/>
              <stop offset="100%" stopColor="#D4A72C" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1c1c1e" strokeDasharray="3 3"/>
          <XAxis dataKey="date" stroke="#6b6b6b" fontSize={10} tickFormatter={s => s?.slice(5)}/>
          <YAxis stroke="#6b6b6b" fontSize={10}/>
          <Tooltip contentStyle={{ background: "#0b0b0c", border: "1px solid rgba(212,167,44,0.35)", borderRadius: 8 }} formatter={v => fmtEUR(v)}/>
          <Area type="monotone" dataKey="revenue" stroke="#E9C46A" strokeWidth={2.2} fill="url(#gRev)"/>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ServiceTypes({ data }) {
  return (
    <div data-testid="services-by-type">
      <div className="text-[11px] text-slate-400 mb-1">Servizi per tipologia</div>
      {data?.length ? (
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0b0b0c", border: "1px solid rgba(212,167,44,0.35)", borderRadius: 8 }}/>
            <Legend wrapperStyle={{ fontSize: 10, color: "#94A3B8" }}/>
          </PieChart>
        </ResponsiveContainer>
      ) : <div className="text-slate-500 text-sm text-center py-12">Nessun dato disponibile</div>}
    </div>
  );
}

function DriverExchanges({ items }) {
  return (
    <ExecPanel title="Servizi da Scambio" sub="Assegnati a te da altri operatori" testid="driver-exchange-section">
      <div className="divide-y divide-white/5">
        {items.map(e => {
          const isDone = e.status === "completed";
          return (
            <div key={e.id} className="px-4 sm:px-5 py-3 flex items-center gap-4" data-testid={`driver-ex-${e.exchange_number}`}>
              <div className="text-center flex-shrink-0">
                <div className="text-[#E9C46A] font-mono-num font-bold text-sm">{e.time || "-"}</div>
                <div className="text-[10px] text-slate-500 font-mono-num">{fmtDate(e.date)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <span className="text-[#E9C46A] font-mono-num text-xs font-bold">{e.exchange_number}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wider ${isDone ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"}`}>{isDone ? "Completato" : "Accettato"}</span>
                  <span className="text-[10px] text-slate-500">da {e.from_operator_name || "-"}</span>
                </div>
                <div className="text-white text-sm truncate">{e.passenger_name || e.customer || "-"} {e.phone && <span className="text-slate-500 text-xs">· {e.phone}</span>}</div>
                <div className="text-xs text-slate-400 flex items-center gap-1 truncate"><MapPin size={11} className="text-emerald-400 flex-shrink-0"/>{e.pickup} <ChevronRight size={11} className="text-slate-600 flex-shrink-0"/><MapPin size={11} className="text-rose-400 flex-shrink-0"/>{e.destination}</div>
              </div>
              <div className="hidden sm:block text-right flex-shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Pax</div>
                <div className="text-slate-200 font-bold">{e.passengers || 1}</div>
              </div>
            </div>
          );
        })}
      </div>
    </ExecPanel>
  );
}

function TodayAgenda({ items, findCustomer, onOpen }) {
  return (
    <ExecPanel title="Calendario di oggi" sub="Agenda" action="Vedi tutto" onAction={onOpen} testid="today-agenda">
      <div className="overflow-y-auto max-h-[340px] divide-y divide-white/5">
        {(items || []).slice(0, 8).map(b => (
          <button key={b.id} onClick={onOpen} className="w-full text-left px-4 py-3 hover:bg-[#D4A72C]/5 transition-colors">
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono-num text-[#E9C46A] font-bold text-sm">{b.time}</div>
              <StatusBadge status={b.status}/>
            </div>
            <div className="text-white text-sm font-bold truncate">{b.passenger_name || findCustomer(b.customer_id)?.name || "-"}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate"><MapPin size={10} className="text-emerald-400 flex-shrink-0"/>{b.pickup} → {b.destination}</div>
          </button>
        ))}
        {(!items || items.length === 0) && <div className="text-slate-500 text-sm text-center py-10">Nessun servizio in programma</div>}
      </div>
    </ExecPanel>
  );
}

function Expirations({ items, unread, nav }) {
  return (
    <ExecPanel title="Notifiche e Avvisi" sub="Scadenze veicoli, autisti e operatore" testid="expirations-section" className="lg:col-span-2">
      {unread > 0 && <div className="px-5 pt-3 text-[11px] text-[#E9C46A]" data-testid="expirations-unread"><Bell size={12} className="inline mr-1"/>{unread} notifiche non lette</div>}
      <div className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
        {items.slice(0, 8).map((it, i) => (
          <div key={i} className="px-4 py-3 flex items-start gap-3 hover:bg-[#D4A72C]/5 cursor-pointer" onClick={() => nav(it.kind === "vehicle" ? "/flotta" : it.kind === "driver" ? "/autisti" : "/impostazioni")}>
            <div className={`p-2 rounded-lg ${it.days <= 7 ? "bg-rose-500/15 text-rose-400" : it.days <= 30 ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-400"}`}>
              {it.kind === "vehicle" ? <Wrench size={14}/> : it.kind === "driver" ? <BadgeCheck size={14}/> : <Shield size={14}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-bold truncate">{it.label || it.type}</div>
              <div className="text-xs text-slate-400 truncate">{it.subject}</div>
            </div>
            <div className={`text-xs font-bold whitespace-nowrap ${it.days <= 7 ? "text-rose-400" : it.days <= 30 ? "text-amber-400" : "text-slate-400"}`}>
              {it.days === 0 ? "Oggi" : it.days === 1 ? "Domani" : `Tra ${it.days}g`}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-slate-500 text-sm text-center">Nessun avviso urgente</div>}
      </div>
    </ExecPanel>
  );
}

function VehicleStatus({ vehicles, onManage }) {
  return (
    <ExecPanel title="Stato Veicoli" sub="Flotta" action="Gestisci" onAction={onManage} testid="vehicle-status">
      <div className="p-4 space-y-2">
        <StatusRow color="emerald" label="Disponibili" value={vehicles.filter(v => v.status === "disponibile").length}/>
        <StatusRow color="amber" label="In servizio" value={vehicles.filter(v => v.status === "in_servizio").length}/>
        <StatusRow color="rose" label="In manutenzione" value={vehicles.filter(v => v.status === "manutenzione" || v.status === "officina").length}/>
        <StatusRow color="slate" label="Non disponibili" value={vehicles.filter(v => ["non_disponibile","fermo"].includes(v.status)).length}/>
        <div className="pt-2 flex items-center justify-between">
          <div className="text-[11px] text-slate-400">Totale flotta</div>
          <div className="text-white font-extrabold text-xl font-mono-num">{vehicles.length}</div>
        </div>
      </div>
    </ExecPanel>
  );
}

function KPI({ icon: Icon, label, value, delta, hint, sparkline }) {
  return (
    <div className="group relative marble-card p-5 overflow-hidden" data-testid="dashboard-kpi-card">
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gold/5 blur-2xl pointer-events-none"/>
      <div className="relative flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl border border-gold/40 bg-gradient-to-br from-gold/15 to-gold/5 text-gold flex items-center justify-center shadow-[0_0_18px_-6px_rgba(214,166,42,0.65)]">
          <Icon size={18} strokeWidth={2.2}/>
        </div>
        {delta != null ? (
          <div className="text-[11px] font-bold flex items-center gap-0.5 text-gold-light font-mono-num px-2 py-0.5 rounded-md bg-gold/5 border border-gold/20">
            <ArrowUpRight size={11}/>{delta > 0 ? "+" : ""}{delta}%
          </div>
        ) : (
          <ArrowUpRight size={14} className="text-gold/40"/>
        )}
      </div>
      <div className="relative mt-5 text-[10px] uppercase tracking-[0.28em] text-gold font-mono-num font-bold">{label}</div>
      <div className="relative text-gold-metal font-black text-3xl font-mono-num mt-1 leading-none">{value}</div>
      <div className="relative text-[11px] text-txt-2 mt-1.5">{hint}</div>
      {sparkline && sparkline.length > 1 && (
        <div className="relative h-9 mt-2 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline}>
              <defs>
                <linearGradient id={`gkpi-${label}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#8E6E1A"/>
                  <stop offset="50%" stopColor="#F1C75B"/>
                  <stop offset="100%" stopColor="#D6A62A"/>
                </linearGradient>
              </defs>
              <Line type="monotone" dataKey="v" stroke={`url(#gkpi-${label})`} strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
function VehiclesKPI({ busy, total, pct }) {
  return (
    <div className="relative marble-card p-5 overflow-hidden" data-testid="dashboard-kpi-vehicles">
      <div className="relative flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl border border-gold/40 bg-gradient-to-br from-gold/15 to-gold/5 text-gold flex items-center justify-center">
          <Car size={18} strokeWidth={2.2}/>
        </div>
        <div className="text-[11px] font-bold text-gold font-mono-num">{pct}%</div>
      </div>
      <div className="relative mt-5 text-[10px] uppercase tracking-[0.28em] text-gold font-mono-num font-bold">Veicoli in servizio</div>
      <div className="relative text-gold-metal font-black text-3xl font-mono-num mt-1 leading-none">{busy} / {total}</div>
      <div className="relative text-[11px] text-txt-2 mt-1.5">Attività flotta</div>
    </div>
  );
}
function NavButton({ onClick, icon: Icon, label }) {
  return (
    <button onClick={onClick} data-testid={`dashboard-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="inline-flex items-center gap-2 rounded-xl border border-[#D4A72C]/30 bg-black/40 text-[#E9C46A] hover:bg-[#D4A72C]/10 hover:border-[#D4A72C]/60 px-4 min-h-[40px] text-sm font-semibold transition-colors">
      <Icon size={15}/>{label}
    </button>
  );
}
function StatusRow({ color, label, value }) {
  const dots = { emerald: "bg-emerald-400", amber: "bg-amber-400", rose: "bg-rose-400", slate: "bg-slate-400" };
  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg exec-tile">
      <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${dots[color]}`}/><span className="text-sm text-slate-300">{label}</span></div>
      <span className="font-mono-num font-extrabold text-white">{value}</span>
    </div>
  );
}

function FinancialSituation({ d }) {
  const toCollect = d?.outstanding_from_customers ?? d?.customers_outstanding ?? 0;
  const toPay = d?.outstanding_to_suppliers ?? d?.suppliers_outstanding ?? 0;
  const saldo = toCollect - toPay;
  const nCust = d?.customers_with_debt ?? d?.outstanding_customers_count ?? null;
  const nSup = d?.suppliers_with_debt ?? d?.outstanding_suppliers_count ?? null;
  return (
    <div data-testid="financial-situation">
      <div className="text-[11px] text-slate-400 mb-2 flex items-center gap-1.5"><Wallet size={13} className="text-[#D4A72C]"/>Situazione finanziaria</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <FinRow icon={Download} tone="emerald" label="Da ricevere" value={fmtEUR(toCollect)} hint={nCust != null ? `${nCust} clienti` : "dai clienti"}/>
        <FinRow icon={Upload} tone="rose" label="Da pagare" value={fmtEUR(toPay)} hint={nSup != null ? `${nSup} fornitori` : "ai fornitori"}/>
        <FinRow icon={CircleDollarSign} tone="gold" label="Saldo previsto" value={fmtEUR(saldo)} hint={saldo >= 0 ? "a nostro favore" : "da coprire"}/>
      </div>
    </div>
  );
}
function FinRow({ icon: Icon, tone, label, value, hint }) {
  const t = {
    emerald: { ring: "border-emerald-500/30", text: "text-emerald-400", num: "text-emerald-300" },
    rose:    { ring: "border-rose-500/30",    text: "text-rose-400",    num: "text-rose-300" },
    gold:    { ring: "border-[#D4A72C]/35",   text: "text-[#E9C46A]",   num: "text-gold-metal" },
  }[tone];
  return (
    <div className={`rounded-xl border ${t.ring} bg-black/35 p-3 flex items-center gap-3 min-w-0`}>
      <Icon size={17} className={`${t.text} flex-shrink-0`} strokeWidth={2.2}/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className={`font-mono-num font-extrabold text-lg ${t.num} leading-tight whitespace-nowrap`}>{value}</div>
        <div className="text-[10px] text-slate-500">{hint}</div>
      </div>
    </div>
  );
}
